import { AnyAction } from 'redux';
import {
  App,
  FamilienstandChanged,
  HausnummerChanged,
  LandChanged,
  OrtChanged,
  PlzChanged,
  Produktlinie,
  StrasseChanged,
  VhvVorbelegungdaten,
} from '@ps20-frontend/frontend-models';
import {
  Auslandsrisiko,
  ExtremwetterschutzSelbstbeteiligung,
  FahrradWert,
  FamilienStand,
  Selbstbeteiligung,
  Stockwerk,
} from '@ps20/bff-models';
import {
  AngebotsId,
  CoordinatesChangedPayload,
  TARIF_CHECKED,
  ZUSATZOPTION_CHECKED,
  ZUSATZOPTION_UNCHECKED,
} from '@ps20-frontend/shop-integration-redux';
import { Injectable } from '@angular/core';
import { VersObjekt } from './reducers/versObjekt.reducer';
import {
  VersicherungssummeChangedAction,
  VersicherungssummeEnum,
  VersicherungssummenArtChangedAction,
  WohnflaecheChangedAction,
} from './model/vertragsoptionen.model';
import {
  SelectedWertsachensummeChangedAction,
  WertsachenSummeChangedAction,
  WertsachensummenChangedAction,
} from './model/wertsachensummen.model';
import { AntragsummerGenerated } from './action-types/antragsummerGenerated';
import {
  VersicherungssummeForLinie,
  VersicherungssummenResponseReceived,
} from './action-types/versicherungssummenResponseReceived';
import { ExtremwetterschutzSelbstbeteiligungValues } from './model/extremwetterschutzSelbstbeteiligungValues.model';
import { EntschaedigungsgrenzeRadForLinie } from './model/entschaedigungsgrenzeRad.model';
import { TragerisikoWerte } from './model/tragerisiko.model';

export interface VhvAction<T = undefined> extends AnyAction {
  payload?: T;
}

export interface ExtremwetterschutzSBReceived {
  angebotsId: string;
  values: ExtremwetterschutzSelbstbeteiligungValues[];
}

@Injectable()
export class VhvActions {
  static readonly ABSPRUNG_INS_DASHBOARD = 'ABSPRUNG_INS_DASHBOARD';
  static readonly ANTRAGSNUMMER_GENERATED = 'ANTRAGSNUMMER_GENERATED';
  static readonly AUSLANDSADRESSE_CHANGED = 'AUSLANDSADRESSE_CHANGED';
  static readonly AUSLANDSRISIKO_CHANGED = 'AUSLANDSRISIKO_CHANGED';
  static readonly BRUTTOPREISE_CALCULATED = 'BRUTTOPREISE_CALCULATED';
  static readonly FAMILIENSTAND_CHANGED = 'FAMILIENSTAND_CHANGED';
  static readonly GEBAEUDE_INNERHALB_WOHNGEBIET_CHANGED = 'GEBAEUDE_INNERHALB_WOHNGEBIET_CHANGED';
  static readonly GEBAEUDE_STAENDIG_BEWOHNT_CHANGED = 'GEBAEUDE_STAENDIG_BEWOHNT_CHANGED';
  static readonly GEBURTSDATUM_CHANGED = 'GEBURTSDATUM_CHANGED';
  static readonly HANDLE_VHV_NAVIGATION_TO_ANGEBOT = 'HANDLE_VHV_NAVIGATION_TO_ANGEBOT';
  static readonly HAUSART_CHANGED = 'HAUSART_CHANGED';
  static readonly MAP_COORDINATES_VHV_CHANGED_EINGABE = 'MAP_COORDINATES_VHV_CHANGED_EINGABE';
  static readonly MAP_COORDINATES_VHV_CHANGED_ANGEBOT = 'MAP_COORDINATES_VHV_CHANGED_ANGEBOT';
  static readonly RESET_ADRESSDATEN_REQUESTED = 'RESET_ADRESSDATEN_REQUESTED';
  static readonly RESET_ADRESSDATEN = 'RESET_ADRESSDATEN';
  static readonly PLZ_CHANGED = 'PLZ_CHANGED';
  static readonly ORT_CHANGED = 'ORT_CHANGED';
  static readonly STRASSE_CHANGED = 'STRASSE_CHANGED';
  static readonly HAUSNUMMER_CHANGED = 'HAUSNUMMER_CHANGED';
  static readonly LAND_CHANGED = 'LAND_CHANGED';
  static readonly PREISE_REQUESTED = 'PREISE_REQUESTED';
  static readonly PRODUKTLINIEN_CHANGED = 'PRODUKTLINIEN_CHANGED';
  static readonly PRODUKT_ZUSATZOPTIONEN_VORBELEGT = 'PRODUKT_VORBELEGT';
  static readonly QUOTEDB_REQUESTED_VHV = 'QUOTEDB_REQUESTED_VHV';
  static readonly SELBSTBETEILIGUNG_CHANGED = 'SELBSTBETEILIGUNG_CHANGED';
  static readonly SELECTED_WERTSACHENSUMME_CHANGED = 'SELECTED_WERTSACHENSUMME_CHANGED';
  static readonly STOCKWERK_CHANGED = 'STOCKWERK_CHANGED';
  static readonly VERSICHERUNGSSUMMEN_RESPONSE_RECEIVED = 'VERSICHERUNGSSUMMEN_RESPONSE_RECEIVED';
  static readonly VERSICHERUNGSSUMME_CHANGED = 'VERSICHERUNGSSUMME_CHANGED';
  static readonly VERSICHERUNGSSUMME_REQUEST_SUCCESSFUL_VHV = 'VERSICHERUNGSSUMME_REQUEST_SUCCESSFUL_VHV';
  static readonly VERSICHERUNGS_OBJEKT_CHANGED = 'VERSICHERUNGS_OBJEKT_CHANGED';
  static readonly VERSSUMME_ART_CHANGED = 'VERSSUMME_ART_CHANGED';
  static readonly VERSSUMME_FAHRRAD_CHANGED = 'VERSSUMME_FAHRRAD_CHANGED';
  static readonly ENTSCHAEDIGUNGSGRENZE_RAD_CHANGED = 'ENTSCHAEDIGUNGSGRENZE_RAD_CHANGED';
  static readonly ENTSCHAEDIGUNGSGRENZE_RAD_RECEIVED = 'ENTSCHAEDIGUNGSGRENZE_RAD_RECEIVED';
  static readonly VORSCHAEDEN_CHANGED = 'VORSCHAEDEN_CHANGED';
  static readonly VORSCHAEDEN_FAHRRAD_CHANGED = 'VORSCHAEDEN_FAHRRAD_CHANGED';
  static readonly VORSCHAEDEN_FELS_CHANGED = 'VORSCHAEDEN_HAUSRAT_CHANGED';
  static readonly VORSCHAEDEN_ELEMENTAR_CHANGED = 'VORSCHAEDEN_ELEMENTAR_CHANGED';
  static readonly SHOW_ELEMENTAR_VORSCHADEN_ERSATZGESCHAEFT = 'SHOW_ELEMENTAR_VORSCHADEN_ERSATZGESCHAEFT';
  static readonly VORVERTRAG_VORSCHAEDEN_DATA_REQUESTED = 'VORVERTRAG_VORSCHAEDEN_DATA_REQUESTED';
  static readonly SHOW_ZUM_ANGEBOT_LOADING_SPINNER = 'SHOW_ZUM_ANGEBOT_LOADING_SPINNER';
  static readonly VORSCHLAGDATEN_CHANGED = 'VORSCHLAGDATEN_CHANGED';
  static readonly WERTSACHENSUMMEN_CHANGED = 'WERTSACHENSUMMEN_CHANGED';
  static readonly WERTSACHENSUMMEN_CHANGED_AFTER_VORBELEGUNG = 'WERTSACHENSUMMEN_CHANGED_AFTER_VORBELEGUNG';
  static readonly WERTSACHENSUMME_CHANGED = 'WERTSACHENSUMME_CHANGED';
  static readonly WOHNFLAECHE_CHANGED = 'WOHNFLAECHE_CHANGED';
  static readonly ZUM_ANGEBOT_BUTTON_CLICKED = 'ZUM_ANGEBOT_BUTTON_CLICKED';
  static readonly FORM_VALIDATION_TRIGGERED = 'FORM_VALIDATION_TRIGGERED';
  static readonly ZURUECK_ZUM_SHOP_MODAL_CLOSE = 'ZURUECK_ZUM_SHOP_MODAL_CLOSE';
  static readonly ZURUECK_ZUM_SHOP_MODAL_OPEN = 'ZURUECK_ZUM_SHOP_MODAL_OPEN';
  static readonly AUSLAENDISCHE_ADRESSE_CHANGED = 'AUSLAENDISCHE_ADRESSE_CHANGED';
  static readonly CLEAN_ADDRESS_FOR_AUSLANDSTARIFIERUNG = 'CLEAN_ADDRESSE_FOR_AUSLAND';
  static readonly EXTREMWETTERSCHUTZ_SELBSTBETEILIGUNG_VALUES_RECEIVED =
    'EXTREMWETTERSCHUTZ_SELBSTBETEILIGUNG_VALUES_RECEIVED';
  static readonly EXTREMWETTERSCHUTZ_SELBSTBETEILIGUNG_CHANGED =
    'EXTREMWETTERSCHUTZ_SELBSTBETEILIGUNG_CHANGED';
  static readonly TRAGERISIKO_CHANGED = 'TRAGERISIKO_CHANGED';

  absprungInsDashboard(): AnyAction {
    return { type: VhvActions.ABSPRUNG_INS_DASHBOARD };
  }

  antragsnummerGenerated(
    antragsnummer: string,
    angebotsId: string,
  ): VhvAction<AntragsummerGenerated> {
    return {
      type: VhvActions.ANTRAGSNUMMER_GENERATED,
      payload: { antragsnummer, angebotsId },
    };
  }

  resetAdressdatenRequested(): VhvAction<void> {
    return { type: VhvActions.RESET_ADRESSDATEN_REQUESTED };
  }

  resetAdressdaten(): VhvAction<void> {
    return { type: VhvActions.RESET_ADRESSDATEN };
  }

  plzChanged(plz: string): VhvAction<PlzChanged> {
    return { type: VhvActions.PLZ_CHANGED, payload: { plz } };
  }

  ortChanged(ort: string): VhvAction<OrtChanged> {
    return { type: VhvActions.ORT_CHANGED, payload: { ort } };
  }

  strasseChanged(strasse: string): VhvAction<StrasseChanged> {
    return { type: VhvActions.STRASSE_CHANGED, payload: { strasse } };
  }

  hausnummerChanged(hausnummer: string): VhvAction<HausnummerChanged> {
    return { type: VhvActions.HAUSNUMMER_CHANGED, payload: { hausnummer } };
  }

  landChanged(land: { text: string; wert: string }): VhvAction<LandChanged> {
    return { type: VhvActions.LAND_CHANGED, payload: land };
  }

  familienstandChanged(familienstand: FamilienStand): VhvAction<FamilienstandChanged> {
    return {
      type: VhvActions.FAMILIENSTAND_CHANGED,
      payload: { familienstand },
    };
  }

  wohnflaecheChanged(wohnflaeche: number): WohnflaecheChangedAction {
    return { type: VhvActions.WOHNFLAECHE_CHANGED, payload: { wohnflaeche } };
  }

  gebaeudeStaendigBewohntChanged(
    gebaeudeStaendigBewohnt: boolean,
  ): VhvAction<{ gebaeudeStaendigBewohnt: boolean }> {
    return {
      type: VhvActions.GEBAEUDE_STAENDIG_BEWOHNT_CHANGED,
      payload: { gebaeudeStaendigBewohnt },
    };
  }

  gebaeudeInnerhalbWohngebietChanged(
    innerhalbWohngebiet: boolean,
  ): VhvAction<{ innerhalbWohngebiet: boolean }> {
    return {
      type: VhvActions.GEBAEUDE_INNERHALB_WOHNGEBIET_CHANGED,
      payload: { innerhalbWohngebiet },
    };
  }

  stockwerkChanged(stockwerk: Stockwerk): VhvAction<{ stockwerk: Stockwerk }> {
    return { type: VhvActions.STOCKWERK_CHANGED, payload: { stockwerk } };
  }

  hausartChanged(hausart: string): VhvAction<{ hausart: string }> {
    return { type: VhvActions.HAUSART_CHANGED, payload: { hausart } };
  }

  geburtsdatumChanged(geburtsdatum: string): VhvAction<{ geburtsdatum: string }> {
    return { type: VhvActions.GEBURTSDATUM_CHANGED, payload: { geburtsdatum } };
  }

  vorschaedenChanged(vorschaeden: boolean): AnyAction {
    return { type: VhvActions.VORSCHAEDEN_CHANGED, payload: { vorschaeden } };
  }

  felsChanged(fels: number): AnyAction {
    return { type: VhvActions.VORSCHAEDEN_FELS_CHANGED, payload: { fels } };
  }

  fahrradChanged(fahrrad: number): AnyAction {
    return {
      type: VhvActions.VORSCHAEDEN_FAHRRAD_CHANGED,
      payload: { fahrrad },
    };
  }

  elementarChanged(elementar: number): AnyAction {
    return { type: VhvActions.VORSCHAEDEN_ELEMENTAR_CHANGED, payload: { elementar } };
  }

  vorvertragVorschaedenDataRequested(angebotsId: string): VhvAction<AngebotsId> {
    return {
      type: VhvActions.VORVERTRAG_VORSCHAEDEN_DATA_REQUESTED,
      payload: { appId: App.VHV_V2, angebotsId },
    };
  }

  showElementarVorschadenErsatzgeschaeft(visible: boolean): VhvAction<boolean> {
    return { type: VhvActions.SHOW_ELEMENTAR_VORSCHADEN_ERSATZGESCHAEFT, payload: visible };
  }

  showZumAngebotLoadingSpinner(show: boolean): VhvAction<boolean> {
    return { type: VhvActions.SHOW_ZUM_ANGEBOT_LOADING_SPINNER, payload: show };
  }

  fahrradVerssummeChanged(fahrrad: FahrradWert): VhvAction<FahrradWert> {
    return { type: VhvActions.VERSSUMME_FAHRRAD_CHANGED, payload: fahrrad };
  }

  entschaedigungsgrenzeRadChanged(betrag: number | null): VhvAction<{ betrag: number | null }> {
    return {
      type: VhvActions.ENTSCHAEDIGUNGSGRENZE_RAD_CHANGED,
      payload: { betrag },
    };
  }

  entschaedigungsgrenzeRadReceived(
    entschaedigungsgrenzen: EntschaedigungsgrenzeRadForLinie[],
  ): VhvAction<{ entschaedigungsgrenzen: EntschaedigungsgrenzeRadForLinie[] }> {
    return {
      type: VhvActions.ENTSCHAEDIGUNGSGRENZE_RAD_RECEIVED,
      payload: { entschaedigungsgrenzen },
    };
  }

  selbstbeteiligungChanged(
    selbstbeteiligung: Selbstbeteiligung,
  ): VhvAction<{ selbstbeteiligung: Selbstbeteiligung }> {
    return {
      type: VhvActions.SELBSTBETEILIGUNG_CHANGED,
      payload: { selbstbeteiligung },
    };
  }

  produktZusatzoptionenVorbelegt(
    vorbelegungdaten: VhvVorbelegungdaten,
  ): VhvAction<VhvVorbelegungdaten> {
    return {
      type: VhvActions.PRODUKT_ZUSATZOPTIONEN_VORBELEGT,
      payload: vorbelegungdaten,
    };
  }

  tarifChecked(index: number): VhvAction<{ index: number }> {
    return { type: TARIF_CHECKED, payload: { index } };
  }

  zusatzoptionChecked(name: string): VhvAction<{ name: string }> {
    return { type: ZUSATZOPTION_CHECKED, payload: { name } };
  }

  zusatzoptionUnchecked(name: string): VhvAction<{ name: string }> {
    return { type: ZUSATZOPTION_UNCHECKED, payload: { name } };
  }

  requestPreis(): VhvAction<void> {
    return { type: VhvActions.PREISE_REQUESTED };
  }

  bruttopreiseCalculated(
    produktlinienMitBruttopreisen: Array<Produktlinie>,
  ): VhvAction<Array<Produktlinie>> {
    return {
      type: VhvActions.BRUTTOPREISE_CALCULATED,
      payload: produktlinienMitBruttopreisen,
    };
  }

  versicherungssummeChanged(versicherungssumme: number): VersicherungssummeChangedAction {
    return {
      type: VhvActions.VERSICHERUNGSSUMME_CHANGED,
      payload: { versicherungssumme },
    };
  }

  versicherungssummenResponseReceived(
    versicherungssummen: VersicherungssummeForLinie[],
    unterversicherungsverzicht: boolean,
  ): VhvAction<VersicherungssummenResponseReceived> {
    return {
      type: VhvActions.VERSICHERUNGSSUMMEN_RESPONSE_RECEIVED,
      payload: { summen: versicherungssummen, unterversicherungsverzicht },
    };
  }

  zumAngebotButtonClicked(): VhvAction {
    return { type: VhvActions.ZUM_ANGEBOT_BUTTON_CLICKED };
  }

  formValidationTriggered(): VhvAction {
    return { type: VhvActions.FORM_VALIDATION_TRIGGERED };
  }

  verssummeArtChanged(verssummeArt: VersicherungssummeEnum): VersicherungssummenArtChangedAction {
    return {
      type: VhvActions.VERSSUMME_ART_CHANGED,
      payload: { verssummeArt },
    };
  }

  versicherungsObjektChanged(
    changedVersicherungsObjekt: Partial<VersObjekt>,
  ): VhvAction<Partial<VersObjekt>> {
    return {
      type: VhvActions.VERSICHERUNGS_OBJEKT_CHANGED,
      payload: changedVersicherungsObjekt,
    };
  }

  wertsachensummenChanged(
    wertsachensummenPayload: WertsachensummenChangedAction['payload'],
  ): WertsachensummenChangedAction {
    return {
      type: VhvActions.WERTSACHENSUMMEN_CHANGED,
      payload: wertsachensummenPayload,
    };
  }

  wertsachensummenChangedAfterVorbelegung(
    wertsachensummenPayload: WertsachensummenChangedAction['payload'],
  ): WertsachensummenChangedAction {
    return {
      type: VhvActions.WERTSACHENSUMMEN_CHANGED_AFTER_VORBELEGUNG,
      payload: wertsachensummenPayload,
    };
  }

  wertsachensummeChanged(
    wertsachensummenPayload: WertsachenSummeChangedAction['payload'],
  ): WertsachenSummeChangedAction {
    return {
      type: VhvActions.WERTSACHENSUMME_CHANGED,
      payload: wertsachensummenPayload,
    };
  }

  selectedWertsachenSummeChanged(
    wertsachensummePayload: SelectedWertsachensummeChangedAction['payload'],
  ): SelectedWertsachensummeChangedAction {
    return {
      type: VhvActions.SELECTED_WERTSACHENSUMME_CHANGED,
      payload: wertsachensummePayload,
    };
  }

  zurueckZumShopModalOpen(): VhvAction<void> {
    return { type: VhvActions.ZURUECK_ZUM_SHOP_MODAL_OPEN };
  }

  zurueckZumShopModalClose(): VhvAction<void> {
    return { type: VhvActions.ZURUECK_ZUM_SHOP_MODAL_CLOSE };
  }

  produktlinienChanged(produktlinien: Produktlinie[]): VhvAction<Produktlinie[]> {
    return { type: VhvActions.PRODUKTLINIEN_CHANGED, payload: produktlinien };
  }

  vorschlagdatenChanged(angebotsId: string): VhvAction<string> {
    return { type: VhvActions.VORSCHLAGDATEN_CHANGED, payload: angebotsId };
  }

  quoteDBRequested(): AnyAction {
    return { type: VhvActions.QUOTEDB_REQUESTED_VHV };
  }

  handleVhvNavigationToAngebot(): VhvAction<void> {
    return { type: VhvActions.HANDLE_VHV_NAVIGATION_TO_ANGEBOT };
  }

  mapCoordinatesChangedEingabe(
    coordinatesChangedPayload: CoordinatesChangedPayload,
  ): VhvAction<CoordinatesChangedPayload> {
    return {
      type: VhvActions.MAP_COORDINATES_VHV_CHANGED_EINGABE,
      payload: coordinatesChangedPayload,
    };
  }

  mapCoordinatesChangedAngebot(
    coordinatesChangedPayload: CoordinatesChangedPayload,
  ): VhvAction<CoordinatesChangedPayload> {
    return {
      type: VhvActions.MAP_COORDINATES_VHV_CHANGED_ANGEBOT,
      payload: coordinatesChangedPayload,
    };
  }

  auslaendischeAdressChecboxChanged(isCheckboxChecked: boolean): VhvAction<boolean> {
    return {
      type: VhvActions.AUSLAENDISCHE_ADRESSE_CHANGED,
      payload: isCheckboxChecked,
    };
  }

  auslandsadresseChanged(auslandsadresse: string): VhvAction<string> {
    return { type: VhvActions.AUSLANDSADRESSE_CHANGED, payload: auslandsadresse };
  }

  auslandsrisikoChanged(auslandsrisiko: Auslandsrisiko): VhvAction<Auslandsrisiko> {
    return { type: VhvActions.AUSLANDSRISIKO_CHANGED, payload: auslandsrisiko };
  }

  cleanAddressForAuslandstarifierung(): VhvAction<void> {
    return { type: VhvActions.CLEAN_ADDRESS_FOR_AUSLANDSTARIFIERUNG };
  }

  extremwetterschutzSelbstbeteiligungReceived(
    extremwetterschutzSelbstbeteiligungValuesList: ExtremwetterschutzSelbstbeteiligungValues[],
    angebotsId: string,
  ): VhvAction<ExtremwetterschutzSBReceived> {
    return {
      type: VhvActions.EXTREMWETTERSCHUTZ_SELBSTBETEILIGUNG_VALUES_RECEIVED,
      payload: { angebotsId, values: extremwetterschutzSelbstbeteiligungValuesList },
    };
  }

  extremwetterschutzSelbstbeteiligungChanged(
    extremwetterschutzSelbstbeteiligung: ExtremwetterschutzSelbstbeteiligung,
    angebotsId: string,
  ): VhvAction<{
    extremwetterschutzSelbstbeteiligung: ExtremwetterschutzSelbstbeteiligung;
    angebotsId: string;
  }> {
    return {
      type: VhvActions.EXTREMWETTERSCHUTZ_SELBSTBETEILIGUNG_CHANGED,
      payload: {
        extremwetterschutzSelbstbeteiligung,
        angebotsId,
      },
    };
  }

  tragerisikoChanged(
    tragerisiko: TragerisikoWerte,
    angebotsId: string,
  ): VhvAction<{
    tragerisiko: TragerisikoWerte;
    angebotsId: string;
  }> {
    return {
      type: VhvActions.TRAGERISIKO_CHANGED,
      payload: {
        tragerisiko,
        angebotsId,
      },
    };
  }
}
